var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Dashboard from "@core/dashboard/Dashboard";
import ErrorBoundary from "@helper/ErrorBoundary/ErrorBoundary";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BaseLayout } from "@core/layout/BaseLayout";
import { getLogo, toJSON } from "@utils";
import { UXPContext } from "@core/context/Context";
import { registerFloatingActionSection, unregisterFloatingActionSection, FloatingActionSectionType, FloatingActionButtonVisibility } from "@core/floating-actions/floatingActionsRegistry";
import FloatingActions from "@core/floating-actions/FloatingActions";
import useToast from "@notifications/Toast/useToast";
import DashboardDeveloperMenu from "@core/dashboard/configurations/DashboardDeveloperMenu";
import DashboardSettingsModal from "@core/dashboard/configurations/DashboardSettingsModal";
import DashboardConfigPanel from "@core/dashboard/configurations/DashboardConfigPanel";
import { DEFAULT_BREAKPOINTS } from "@core/dashboard/dashboardUtils";
import { WidgetDrawerType } from "@core/dashboard/widget-drawer/WidgetDrawer";
const DashboardViewBase = ({}) => {
    const context = useContext(UXPContext);
    const toast = useToast();
    const [widgets, setWidgets] = useState([]);
    const [layouts, setLayouts] = useState({});
    const [navigationLinks, setNavigationLinks] = useState({ all: [], top: {}, sidebar: [] });
    const [expandedSidebar, setExpandedSidebar] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [showDeveloperMenu, setShowDeveloperMenu] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [gridSettings, setGridSettings] = useState(() => toJSON(localStorage.getItem('uxp-grid-settings'), null));
    const [currentColumnsCount, setCurrentColumnsCount] = useState(40);
    const effectiveBreakpoints = useMemo(() => {
        if (!(gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.columns))
            return DEFAULT_BREAKPOINTS;
        return Object.fromEntries(Object.entries(DEFAULT_BREAKPOINTS).map(([k, v]) => [k, Object.assign(Object.assign({}, v), { columns: gridSettings.columns })]));
    }, [gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.columns]);
    const [selectedBreakpoint, setSelectedBreakpoint] = useState(null);
    const floatingActionsControlsRef = useRef(null);
    const dashboardContainerRef = useRef(null);
    const loadConfiguration = useCallback(() => {
        const widgets = toJSON(localStorage.getItem('uxp-widgets'), []);
        const layouts = toJSON(localStorage.getItem('uxp-widget-layouts'), {});
        setWidgets(widgets || []);
        setLayouts(layouts || {});
    }, []);
    const saveConfiguration = useCallback((widgets, layouts) => {
        localStorage.setItem('uxp-widgets', JSON.stringify(widgets));
        localStorage.setItem('uxp-widget-layouts', JSON.stringify(layouts));
    }, []);
    const handleClearWidgets = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        localStorage.setItem('uxp-widgets', JSON.stringify([]));
        localStorage.setItem('uxp-widget-layouts', JSON.stringify({}));
        setWidgets([]);
        setLayouts({});
        toast.success(context.$L('uxp-core.text.changes-saved'));
    }), [context]);
    const handleSaveGridSettings = useCallback((settings) => __awaiter(void 0, void 0, void 0, function* () {
        localStorage.setItem('uxp-grid-settings', JSON.stringify(settings));
        setGridSettings(settings);
        toast.success(context.$L('uxp-core.text.changes-saved'));
    }), [context]);
    const loadNavigation = useCallback(() => {
        const placeholderLinks = [
            {
                id: 'home',
                label: context.$L('uxp-core.text.home'),
                icon: 'fal home',
                link: '#'
            }
        ];
        setNavigationLinks({
            all: [...placeholderLinks],
            top: {
                ['uxp_dev']: {
                    link: {
                        id: 'uxp_dev',
                        label: context.$L('uxp-core.text.experience-portal'),
                        icon: "",
                        link: ""
                    },
                    parent: null,
                    children: []
                }
            },
            sidebar: placeholderLinks
        });
    }, [context]);
    useEffect(() => {
        loadConfiguration();
    }, [loadConfiguration]);
    useEffect(() => {
        loadNavigation();
    }, [loadNavigation]);
    const handleSidebarToggle = useCallback(() => {
        setExpandedSidebar(prev => { return !prev; });
    }, [context]);
    const handleExitEdit = useCallback(() => {
        // Save current state
        saveConfiguration(widgets, layouts);
        // Exit edit mode
        setIsEditing(false);
    }, [widgets, layouts, saveConfiguration]);
    const handleWidgetChanges = useCallback((widgets, layouts) => __awaiter(void 0, void 0, void 0, function* () {
        setWidgets(widgets);
        setLayouts(layouts);
        saveConfiguration(widgets, layouts);
        return true;
    }), [saveConfiguration]);
    const handleWidgetDrawerChanges = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        // Handle UI selection - open in new tab
        if (event.type === WidgetDrawerType.UIs) {
            event.items.forEach((ui) => {
                const uiItem = ui;
                const url = `${window.location.origin}/ui.html?id=${encodeURIComponent(uiItem.id)}`;
                window.open(url, '_blank');
            });
            return true;
        }
        // update widgets and layouts
        if (event.widgets && event.layouts) {
            handleWidgetChanges(event.widgets, event.layouts);
        }
        return true;
    }), [handleWidgetChanges]);
    const handleBreakpointSelect = useCallback((breakpoint) => {
        setSelectedBreakpoint(breakpoint);
    }, []);
    const handleContainerMount = useCallback((ref) => {
        dashboardContainerRef.current = ref;
    }, []);
    const customActions = useMemo(() => [
        {
            icon: 'phl gear',
            onClick: () => setShowSettingsModal(prev => !prev),
            tooltip: context.$L('uxp-core.text.settings'),
            variant: 'secondary',
            visibility: FloatingActionButtonVisibility.Edit
        },
        {
            icon: 'phl code',
            onClick: () => setShowDeveloperMenu(prev => !prev),
            tooltip: context.$L('uxp-core.text.developer-settings'),
            variant: 'secondary',
            visibility: FloatingActionButtonVisibility.Edit
        }
    ], [context]);
    // Register dashboard floating action section
    useEffect(() => {
        registerFloatingActionSection({
            id: 'dashboard',
            type: FloatingActionSectionType.Dashboard,
            widgets: widgets,
            layouts: layouts,
            enableEdit: true,
            isEditing: isEditing,
            onEnterEditMode: () => setIsEditing(true),
            onExitEditMode: handleExitEdit,
            enableAdd: true,
            addButtonVisibility: FloatingActionButtonVisibility.Always,
            onChangeWidgetDrawer: handleWidgetDrawerChanges,
            customActions: customActions,
            isBounded: false,
            maxColumns: currentColumnsCount,
            widgetDrawerConfig: {
                defaultType: WidgetDrawerType.Widgets,
                showTypeFilter: true,
                widgetsButtonLabel: context.$L('uxp-core.wm.add-to-dashboard'),
                uisButtonLabel: context.$L('uxp-core.text.open-ui'),
                enableUIs: true
            },
            onRegister: (controls) => {
                floatingActionsControlsRef.current = controls;
            }
        });
        return () => {
            unregisterFloatingActionSection('dashboard');
            floatingActionsControlsRef.current = null;
        };
    }, [isEditing, widgets, layouts, customActions, handleWidgetDrawerChanges, handleExitEdit, currentColumnsCount, context]);
    return _jsxs(BaseLayout, { header: {
            logo: getLogo(context),
            expandedSidebar: expandedSidebar,
            onToggleSidebar: handleSidebarToggle,
            navigationLinks: navigationLinks,
            renderRightSection: _jsxs(_Fragment, { children: [" ", " "] })
        }, sidebar: {
            navigationLinks: navigationLinks,
            loading: false,
            tools: {
                search: { enable: true, onClick: () => { } },
                marketPlace: {
                    enable: true,
                    url: 'https://spaceworx.com'
                }
            }
        }, children: [_jsxs("div", { className: "uxpcore_dashboardview__container", children: [_jsx(Dashboard, { widgets: widgets || [], layouts: layouts || {}, margin: (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.margin) != null ? [gridSettings.margin, gridSettings.margin] : undefined, padding: (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.containerPadding) != null ? [gridSettings.containerPadding, gridSettings.containerPadding] : undefined, isEditing: isEditing, onSave: handleWidgetChanges, allowFreePositioning: (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.allowFreePositioning) === true, allowRearrange: true, showSpacerWidget: false, showGridlines: (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.showGridlines) === true, emptyPlaceholder: {
                            show: (gridSettings === null || gridSettings === void 0 ? void 0 : gridSettings.showEmptyPlaceholder) !== false,
                            onClick: () => { var _a; return (_a = floatingActionsControlsRef.current) === null || _a === void 0 ? void 0 : _a.openWidgetDrawer(); }
                        }, onChangeBreakPoint: setCurrentColumnsCount, breakpointOverride: (selectedBreakpoint === null || selectedBreakpoint === void 0 ? void 0 : selectedBreakpoint.key) || null, onContainerMount: handleContainerMount, breakpoints: effectiveBreakpoints }), _jsx(DashboardSettingsModal, { show: showSettingsModal, onClose: () => setShowSettingsModal(false), onClearWidgets: handleClearWidgets, onSaveGridSettings: handleSaveGridSettings, gridSettings: gridSettings }), showDeveloperMenu && (_jsx(DashboardDeveloperMenu, { show: showDeveloperMenu, onClose: () => setShowDeveloperMenu(false) })), isEditing && (_jsx(DashboardConfigPanel, { containerRef: dashboardContainerRef, breakpointsSelection: {
                            enable: true,
                            selected: (selectedBreakpoint === null || selectedBreakpoint === void 0 ? void 0 : selectedBreakpoint.key) || null,
                            onSelect: handleBreakpointSelect,
                            breakpoints: DEFAULT_BREAKPOINTS
                        }, userGroupsSelection: {
                            enable: false,
                            selected: null,
                            onSelect: () => { }
                        }, dashboardSelection: {
                            enable: false,
                            ids: [],
                            selected: null,
                            onSelect: () => { }
                        } }))] }), _jsx(FloatingActions, {})] });
};
const DashboardView = memo((props) => (_jsx(ErrorBoundary, { children: _jsx(DashboardViewBase, Object.assign({}, props)) })));
export default DashboardView;
